import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  DataState,
  InfoRow,
  StatusBadge,
  Table,
  formatDate,
  formatLapTime,
  type Column,
} from "@vms/ui-web";
import { GAME_LABELS } from "@vms/types";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";
import { useApi } from "../../auth/AuthProvider";

interface SessionWithStats {
  id: string;
  driverId: string;
  driverName: string | null;
  simulatorId: string | null;
  simulatorName: string | null;
  car: string | null;
  carName?: string | null;
  track: string | null;
  trackName?: string | null;
  status: "pending" | "loading" | "active" | "completed" | "cancelled";
  cancelReason: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  bestLapMs: number | null;
  lapCount: number;
  finalPosition: number | null;
}

interface SimEntry {
  id: string;
  name: string | null;
  used: boolean;
}

interface EventDetailPayload {
  event: {
    id: string;
    name: string;
    mode: "hotlap" | "race";
    status: "draft" | "active" | "completed" | "deleted" | null;
    track: string | null;
    trackName?: string | null;
    game: string | null;
    /** Hub-authored key backing the public leaderboard URL (`/l/:publicKey`). */
    publicKey?: string | null;
    /** Public leaderboard toggle — absent/undefined ⇒ ON; only explicit `false` disables. */
    publicLeaderboardEnabled?: boolean;
    startDate?: string | null;
    endDate?: string | null;
    simulatorAllocation?: number | null;
    numberGuests?: number | null;
    carsAvailable?: string[];
    carNames?: string[];
    createdAt: string;
  };
  sessions: SessionWithStats[];
  simulators: {
    allocated: SimEntry[];
    usedNotAllocated: SimEntry[];
  };
}

function gameLabel(game: string | null | undefined): string {
  if (!game) return "—";
  return (GAME_LABELS as Record<string, string>)[game] ?? game;
}

export function VenueEventDetail() {
  const { vslug, eventId } = useParams<{ vslug: string; eventId: string }>();
  const { currentOrg, currentVenue } = useOrgContext();
  const api = useApi();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const detail = useQuery<EventDetailPayload>(
    currentVenue && eventId ? `/api/venues/${currentVenue.id}/events/${eventId}` : null,
  );

  const data = detail.data;

  /**
   * Download the event-export XLSX via the authenticated API client so the
   * Bearer token is attached. A bare `window.location.assign(...)` would do
   * a top-level navigation without our in-memory token, hitting the 401
   * "Missing bearer token" path on the cloud.
   */
  async function downloadExport() {
    if (!data || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const res = await api(`/api/events/${data.event.id}/export`);
      if (!res.ok) {
        const json = await res.json().catch(() => ({}) as { error?: string });
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      // Server sets Content-Disposition with the canonical filename; honour it
      // when present so the file lands with the sanitised name + date stamp.
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = /filename="([^"]+)"/.exec(disposition);
      const filename = match?.[1] ?? `${data.event.name || "event"}.xlsx`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so the browser has time to start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  const positionedSessions = useMemo(() => {
    if (!data || data.event.mode !== "race") return [];
    return [...data.sessions]
      .filter((s) => s.finalPosition != null)
      .sort((a, b) => (a.finalPosition ?? 999) - (b.finalPosition ?? 999));
  }, [data]);

  if (!currentOrg || !currentVenue) return null;

  const backUrl = `/org/${currentOrg.slug}/venue/${vslug}/events`;

  // Public, key-gated leaderboard link operators can share / QR. Absolute so
  // it's copy-pasteable anywhere; the `/l/:publicKey` route is public (no login).
  // Hidden when the operator has turned the public leaderboard off for this event
  // (the `/l/:key` route 404s in that case, so don't surface a dead link).
  const publicLeaderboardPath =
    data?.event.publicKey && data.event.publicLeaderboardEnabled !== false
      ? `/l/${data.event.publicKey}`
      : null;
  const publicLeaderboardUrl = publicLeaderboardPath
    ? `${window.location.origin}${publicLeaderboardPath}`
    : null;

  // Authed live leaderboard display — mirror of the hub's "Live Display", and
  // the cloud-ui equivalent of hub-ui's /display/* pages. Uses the internal
  // event id + session JWT, so it works immediately (no public_key needed).
  const liveDisplayPath = data
    ? `/display/${data.event.mode === "race" ? "race" : "hotlap"}/${data.event.id}`
    : null;

  // Team-facing results table (finishing order), event preselected.
  const resultsPath = data
    ? `/org/${currentOrg.slug}/venue/${vslug}/results?eventId=${data.event.id}`
    : null;

  async function copyPublicLink() {
    if (!publicLeaderboardUrl) return;
    try {
      await navigator.clipboard.writeText(publicLeaderboardUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked (insecure context) — no-op */
    }
  }

  return (
    <div>
      <DataState
        loading={detail.loading}
        error={detail.error}
        empty={!detail.loading && !data}
        emptyMessage="Event not found."
      >
        {data && (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <Button variant="secondary" size="sm" to={backUrl}>
                ← Back to Events
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">{data.event.name}</h1>
              <StatusBadge domain="event" status={data.event.status ?? "draft"} />
              <StatusBadge domain="mode" status={data.event.mode} />
              <div className="ml-auto flex items-start gap-2">
                {/* Team-facing results table (finishing order + sectors). */}
                {resultsPath && (
                  <Button variant="secondary" size="sm" to={resultsPath}>
                    Results
                  </Button>
                )}
                {/* Live leaderboard display — always available for the operator
                    (authed, internal event id). */}
                {liveDisplayPath && (
                  <a
                    href={liveDisplayPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
                    title="Open the live leaderboard display in a new tab"
                  >
                    Live Display
                  </a>
                )}
                {/* Public, key-gated share link — appears once the event's
                    public_key has synced from the hub (restart hub + cloud so
                    migrations 068/028 run and the key propagates). */}
                {publicLeaderboardPath && (
                  <>
                    <a
                      href={publicLeaderboardPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      title="Open the public (no-login) leaderboard in a new tab"
                    >
                      Public link
                    </a>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={copyPublicLink}
                      title={publicLeaderboardUrl ?? undefined}
                    >
                      {copied ? "Copied!" : "Copy link"}
                    </Button>
                  </>
                )}
                <div className="flex flex-col items-end">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={downloadExport}
                    loading={exporting}
                    title={
                      data.event.status === "completed"
                        ? "Download a multi-sheet Excel workbook of this event's data"
                        : "Partial — event still in progress"
                    }
                  >
                    Export to Excel
                  </Button>
                  {data.event.status !== "completed" && (
                    <span className="text-xs text-amber-600 mt-1">Partial — event still in progress</span>
                  )}
                  {exportError && (
                    <span className="text-xs text-red-600 mt-1">{exportError}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Top info row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <Card className="p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Event
                </h2>
                <dl className="space-y-2 text-sm">
                  <InfoRow label="Status" value={data.event.status ?? "draft"} />
                  <InfoRow label="Mode" value={data.event.mode} />
                  <InfoRow label="Game" value={gameLabel(data.event.game)} />
                  <InfoRow
                    label="Track"
                    value={data.event.trackName ?? data.event.track ?? "—"}
                  />
                  {data.event.startDate && (
                    <InfoRow label="Start" value={formatDate(data.event.startDate)} />
                  )}
                  {data.event.endDate && (
                    <InfoRow label="End" value={formatDate(data.event.endDate)} />
                  )}
                  <InfoRow
                    label="Sims allocated"
                    value={String(data.simulators.allocated.length)}
                  />
                  <InfoRow
                    label="Drivers expected"
                    value={String(data.event.numberGuests ?? "—")}
                  />
                </dl>
              </Card>

              <Card className="p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Simulators
                </h2>
                {data.simulators.allocated.length === 0 ? (
                  <p className="text-sm text-gray-400">No sims allocated.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {data.simulators.allocated.map((s) => (
                      <li key={s.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-900">{s.name ?? s.id}</span>
                        <Badge
                          className={
                            s.used
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }
                        >
                          {s.used ? "used" : "allocated"}
                        </Badge>
                      </li>
                    ))}
                    {data.simulators.usedNotAllocated.map((s) => (
                      <li key={s.id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-900">{s.name ?? s.id}</span>
                        <Badge className="bg-amber-100 text-amber-700">
                          used (unallocated)
                        </Badge>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card className="p-5">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Cars
                </h2>
                {data.event.mode === "hotlap" ? (
                  <div className="text-sm text-gray-700">
                    Locked car:{" "}
                    <span className="font-medium text-gray-900">
                      {data.event.carNames?.[0] ?? data.event.carsAvailable?.[0] ?? "—"}
                    </span>
                    <p className="mt-1 text-xs text-gray-500">
                      Hotlap mode — every driver runs the same car.
                    </p>
                  </div>
                ) : (data.event.carsAvailable ?? []).length === 0 ? (
                  <p className="text-sm text-gray-400">No cars listed.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {(data.event.carNames ?? data.event.carsAvailable ?? []).map((label, i) => (
                      <li key={`${label}-${i}`} className="text-gray-900">
                        {label}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            {/* Race results — race mode only */}
            {data.event.mode === "race" && positionedSessions.length > 0 && (
              <Card className="p-5 mb-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Final positions
                </h2>
                <Table<SessionWithStats>
                  columns={positionColumns(currentOrg.slug, vslug ?? "")}
                  data={positionedSessions}
                  keyExtractor={(s) => s.id}
                />
              </Card>
            )}

            {/* All sessions */}
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Sessions ({data.sessions.length})
              </h2>
              {data.sessions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  No sessions recorded for this event.
                </p>
              ) : (
                <Table<SessionWithStats>
                  columns={sessionColumns(currentOrg.slug, vslug ?? "")}
                  data={data.sessions}
                  keyExtractor={(s) => s.id}
                />
              )}
            </Card>
          </>
        )}
      </DataState>
    </div>
  );
}

function positionColumns(orgSlug: string, vslug: string): Column<SessionWithStats>[] {
  return [
    {
      key: "position",
      header: "Pos",
      render: (s) => (
        <span className="font-semibold text-gray-900">
          {s.finalPosition ? `P${s.finalPosition}` : "—"}
        </span>
      ),
    },
    {
      key: "driver",
      header: "Driver",
      render: (s) => (
        <Link
          to={`/org/${orgSlug}/venue/${vslug}/sessions/${s.id}`}
          className="text-indigo-600 hover:text-indigo-800 font-medium"
        >
          {s.driverName ?? `${s.driverId.slice(0, 8)}…`}
        </Link>
      ),
    },
    {
      key: "sim",
      header: "Simulator",
      render: (s) => s.simulatorName ?? "—",
    },
    {
      key: "car",
      header: "Car",
      render: (s) => s.carName ?? s.car ?? "—",
    },
    {
      key: "laps",
      header: "Laps",
      render: (s) => String(s.lapCount),
    },
    {
      key: "best",
      header: "Best lap",
      render: (s) =>
        s.bestLapMs ? <span className="font-mono">{formatLapTime(s.bestLapMs)}</span> : "—",
    },
  ];
}

function sessionColumns(orgSlug: string, vslug: string): Column<SessionWithStats>[] {
  return [
    {
      key: "driver",
      header: "Driver",
      render: (s) => (
        <Link
          to={`/org/${orgSlug}/venue/${vslug}/sessions/${s.id}`}
          className="text-indigo-600 hover:text-indigo-800 font-medium"
        >
          {s.driverName ?? `${s.driverId.slice(0, 8)}…`}
        </Link>
      ),
    },
    {
      key: "car",
      header: "Car",
      render: (s) => s.carName ?? s.car ?? "—",
    },
    {
      key: "track",
      header: "Track",
      render: (s) => s.trackName ?? s.track ?? "—",
    },
    {
      key: "sim",
      header: "Simulator",
      render: (s) => s.simulatorName ?? "—",
    },
    {
      key: "status",
      header: "Status",
      render: (s) => (
        <div className="flex items-center gap-1">
          <StatusBadge domain="session" status={s.status} />
          {s.cancelReason && (
            <span className="text-xs text-gray-500">· {s.cancelReason}</span>
          )}
        </div>
      ),
    },
    {
      key: "laps",
      header: "Laps",
      render: (s) => String(s.lapCount),
    },
    {
      key: "best",
      header: "Best lap",
      render: (s) =>
        s.bestLapMs ? <span className="font-mono">{formatLapTime(s.bestLapMs)}</span> : "—",
    },
    {
      key: "createdAt",
      header: "Started",
      render: (s) => <span className="text-xs text-gray-500">{formatDate(s.createdAt)}</span>,
    },
  ];
}
